import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import api from '../services/api'
import { isStaffOnlyRoute } from '../utils/planRouteGuards'
import { defaultWorkspaceRoute } from '../utils/workspaceTipo'
import { clearUserScopedLocalStorage } from '../utils/userSessionCache'
import { clearAllSuperAdminPreviews } from './useSuperAdminWorkspacePreview'

async function canAccessWorkspaceStaffRoutes(workspaceId) {
  try {
    const { data } = await api.get(`/workspace-acesso/workspace/${workspaceId}/kanban-permissions/me`)
    const role = String(data?.roleWorkspace || 'MEMBER').toUpperCase()
    return ['OWNER', 'ADMIN'].includes(role)
  } catch {
    return false
  }
}

// Estado global dos workspaces - verdadeiramente global
const workspaces = ref([])
const isLoading = ref(false)
const isInitialized = ref(false)
const lastLoadTime = ref(0)
const cachedUserId = ref(null) // ✅ NOVO: Guarda o ID do usuário do cache

export function resetGlobalWorkspacesState() {
  workspaces.value = []
  isInitialized.value = false
  cachedUserId.value = null
  lastLoadTime.value = 0
  isLoading.value = false
  clearAllSuperAdminPreviews()
}

export function useGlobalWorkspaces() {
  const router = useRouter()
  const route = useRoute()
  const authStore = useAuthStore()

  // Computed para determinar o workspace atual baseado na rota (com cache)
  const currentWorkspace = computed(() => {
    const path = route.path
    const paramsId = route.params.id
    
    // Se estamos em uma rota de workspace, encontrar o workspace correspondente
    if (path.startsWith('/workspace/') && paramsId) {
      return workspaces.value.find(w => w.id == paramsId) || null
    }
    return null
  })

  // Computed para determinar se estamos na home (dashboard) - com cache
  const isHome = computed(() => {
    const path = route.path
    // Estamos no home se estivermos no dashboard principal ou em qualquer página do sistema (não workspace)
    return path === '/dashboard' || 
           path === '/system-dashboard' || 
           path === '/workspaces' || 
           path === '/workspace-permissoes' || 
           path === '/agenda' || 
           path === '/historia' || 
           path === '/relatorios' ||
           path === '/financeiro' ||
           path === '/upgrade' ||
           path === '/meu-perfil' ||
           path === '/estruturacao' ||
           path.startsWith('/estruturacao/') ||
           path === '/suporte' ||
           path.startsWith('/super-admin')
  })

  // Carregar workspaces - SEMPRE DO BANCO
  const loadWorkspaces = async (force = false) => {
    console.log('[WORKSPACES] 🔄 CARREGANDO DO BANCO...')
    console.log('[WORKSPACES] isInitialized:', isInitialized.value)
    console.log('[WORKSPACES] isLoading:', isLoading.value)
    console.log('[WORKSPACES] force:', force)
    
    // ✅ VALIDAR CACHE POR USUÁRIO
    const currentUserId = authStore?.user?.id
    console.log('[WORKSPACES] 👤 Usuário atual:', currentUserId)
    console.log('[WORKSPACES] 💾 Usuário em cache:', cachedUserId.value)
    
    // Se mudou de usuário, limpar cache e forçar reload
    if (cachedUserId.value && cachedUserId.value !== currentUserId) {
      console.log('[WORKSPACES] 🔄 USUÁRIO DIFERENTE! Limpando cache...')
      workspaces.value = []
      isInitialized.value = false
      cachedUserId.value = null
      clearUserScopedLocalStorage()
      resetGlobalWorkspacesState()
      force = true
    }
    
    // Se já está carregando, aguardar
    if (isLoading.value) {
      console.log('[WORKSPACES] ⚠️ Já está carregando, aguardando...')
      return
    }
    
    // Se já foi carregado para ESTE usuário e não é forçado, não recarregar
    if (isInitialized.value && !force && cachedUserId.value === currentUserId) {
      console.log('[WORKSPACES] ✅ Workspaces já carregados para este usuário, usando cache:', workspaces.value.length)
      return
    }

    try {
      isLoading.value = true
      console.log('[WORKSPACES] 📡 Buscando workspaces do BANCO DE DADOS...')
      
      // Verificar se há token válido
      if (!authStore?.token) {
        console.log('[WORKSPACES] ❌ Nenhum token encontrado')
        workspaces.value = []
        isInitialized.value = true
        return
      }
      
      console.log('[WORKSPACES] 🔑 Token encontrado, fazendo SELECT no banco...')
      
      // 🔥 ADICIONAR TIMESTAMP PARA FORÇAR RELOAD E EVITAR CACHE DO NAVEGADOR
      const timestamp = Date.now()
      const response = await api.get(`/workspaces?_t=${timestamp}`)
      
      console.log('[WORKSPACES] 📊 Resposta do banco recebida:', response.data?.length, 'workspaces')
      
      // SEMPRE atualizar do banco
      workspaces.value = response.data || []
      isInitialized.value = true
      lastLoadTime.value = timestamp
      cachedUserId.value = currentUserId // ✅ SALVAR O ID DO USUÁRIO NO CACHE
      
      console.log('[WORKSPACES] ✅ Workspaces carregados DO BANCO com sucesso:', workspaces.value.length)
      console.log('[WORKSPACES] 💾 Cache salvo para usuário:', cachedUserId.value)
      console.log('[WORKSPACES] 📸 Workspaces com foto:', workspaces.value.filter(w => w.fotoWorkspace).length)
      
      // Log detalhado de cada workspace
      workspaces.value.forEach(w => {
        console.log(`[WORKSPACES] 📋 Workspace ${w.id} - ${w.nome}:`, {
          fotoWorkspace: w.fotoWorkspace,
          icone: w.icone,
          temFoto: !!w.fotoWorkspace
        })
      })
      
    } catch (error) {
      console.error('[WORKSPACES] ❌ Erro ao carregar workspaces:', error)
      console.error('[WORKSPACES] ❌ Detalhes:', error.response?.data)
      
      workspaces.value = []
      isInitialized.value = true
      cachedUserId.value = currentUserId // ✅ Marcar como inicializado mesmo com erro
      
    } finally {
      isLoading.value = false
    }
  }

  // Recarregar workspaces (para quando criar/editar/deletar)
  const reloadWorkspaces = async () => {
    isInitialized.value = false
    await loadWorkspaces()
  }

  // Handlers de navegação
  const selectWorkspace = async (workspace) => {
    const workspaceId = workspace?.id
    if (!workspaceId) {
      router.push('/dashboard')
      return
    }

    const defaultRoute = defaultWorkspaceRoute(workspace) || `/workspace/${workspaceId}/dashboard`
    const rawCached = localStorage.getItem(`workspace-last-route-${workspaceId}`)
    const cachedRoute = rawCached ? rawCached.split('?')[0] : null
    const workspacePrefix = `/workspace/${workspaceId}/`

    if (cachedRoute && cachedRoute.startsWith(workspacePrefix)) {
      if (isStaffOnlyRoute(cachedRoute)) {
        const canStaff = await canAccessWorkspaceStaffRoutes(workspaceId)
        router.push(canStaff ? cachedRoute : defaultRoute)
        return
      }
      router.push(cachedRoute)
      return
    }

    router.push(defaultRoute)
  }

  const goHome = () => {
    router.push('/dashboard')
  }

  const openCreateModal = () => {
    // Este método será sobrescrito pelos componentes que usam o modal
    console.log('openCreateModal chamado - deve ser implementado pelo componente pai')
  }

  // Atualizar workspace na lista local (para edições)
  const updateWorkspaceInList = (updatedWorkspace) => {
    const index = workspaces.value.findIndex(w => w.id === updatedWorkspace.id)
    if (index !== -1) {
      workspaces.value[index] = updatedWorkspace
    }
  }

  // Remover workspace da lista local (para exclusões)
  const removeWorkspaceFromList = (workspaceId) => {
    console.log('[COMPOSABLE] removeWorkspaceFromList chamado com ID:', workspaceId)
    console.log('[COMPOSABLE] Workspaces antes:', workspaces.value.length)
    workspaces.value = workspaces.value.filter(w => w.id !== workspaceId)
    console.log('[COMPOSABLE] Workspaces depois:', workspaces.value.length)
  }

  return {
    // Estado
    workspaces,
    currentWorkspace,
    isHome,
    isLoading,
    isInitialized,
    
    // Métodos
    loadWorkspaces,
    reloadWorkspaces,
    selectWorkspace,
    goHome,
    openCreateModal,
    updateWorkspaceInList,
    removeWorkspaceFromList
  }
}
